import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Image, LayoutChangeEvent, Platform, Text, View } from 'react-native';
import { Colors } from '../../../../themes/colors';
import { Icon } from '../../icons';
import { QrIcon } from '../../icons/qrIcon';
import { colors, spacing, tokens, typography } from '../../tokens';
import { Pill } from '../Pill/Pill';
import { StatusTag } from '../Tag/StatusTag';
import { TypeIcon } from '../Type/TypeIcon';
import type { TypeVariant } from '../Type/types';
import { themedStyleSheet } from '../../../../themes/themedStyleSheet';

export type ActivityCardProps = {
  /** Workspace name, shown when user has access to multiple workspaces */
  workspaceName?: string;
  /** Asset name displayed in the header pill */
  assetName: string;
  /** Whether to show the asset name pill (default: true) */
  showAssetName?: boolean;
  /** Cover image URL for the asset */
  imageUrl?: string;
  /** Whether to show the asset thumbnail image (default: true) */
  showImage?: boolean;
  /** Activity title */
  title: string;
  /** Formatted date string (e.g. "Mon", "2h ago") */
  date: string;
  /** Description or latest message content */
  description?: string | React.ReactNode;
  /** Labels to display below the description */
  labels?: { id: string; name: string }[];
  /** Maximum number of labels shown before "+N" overflow indicator */
  maxVisibleLabels?: number;
  /** Severity/type variant for the icon indicator */
  severityVariant: TypeVariant;
  /** Whether the activity has a reminder set */
  hasReminder?: boolean;
  /** Whether the activity is pinned */
  isPinned?: boolean;
  /** Whether the activity is unread */
  isUnread?: boolean;
  /** Whether the card is currently selected */
  isSelected?: boolean;
  /** Optional border radius override */
  borderRadius?: number;
  /** Optional action rendered at the trailing edge of the title row (e.g. call button) */
  trailingAction?: React.ReactNode;
};

export const ActivityCard: React.FC<ActivityCardProps> = ({
  workspaceName,
  assetName,
  showAssetName = true,
  imageUrl,
  showImage = true,
  title,
  date,
  description,
  labels,
  maxVisibleLabels = 2,
  severityVariant,
  hasReminder = false,
  isPinned = false,
  isUnread = false,
  isSelected = false,
  borderRadius: borderRadiusProp,
  trailingAction,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const [labelWidths, setLabelWidths] = useState<number[]>([]);
  const labelWidthsRef = useRef<number[]>([]);

  const labelKey = useMemo(
    () => labels?.map((l) => l.id).join(',') ?? '',
    [labels]
  );

  useEffect(() => {
    labelWidthsRef.current = [];
    setLabelWidths([]);
  }, [labelKey]);

  const hoverProps =
    Platform.OS === 'web'
      ? {
          onMouseEnter: () => setIsHovered(true),
          onMouseLeave: () => setIsHovered(false),
        }
      : {};

  const gap = spacing.sm;
  const overflowTagWidth = 36;

  const computeVisibleLabels = () => {
    if (!labels || labels.length === 0)
      return { visible: [], overflowCount: 0 };
    if (containerWidth === 0 || labelWidths.length < labels.length) {
      return {
        visible: labels.slice(0, maxVisibleLabels),
        overflowCount: Math.max(0, labels.length - maxVisibleLabels),
      };
    }

    let used = 0;
    let count = 0;

    for (let i = 0; i < labels.length; i++) {
      const w = labelWidths[i];
      const gapBefore = i > 0 ? gap : 0;
      const remaining = labels.length - i - 1;
      const reserve = remaining > 0 ? overflowTagWidth + gap : 0;

      if (used + gapBefore + w + reserve <= containerWidth) {
        used += gapBefore + w;
        count++;
      } else {
        break;
      }
    }

    return {
      visible: labels.slice(0, count),
      overflowCount: labels.length - count,
    };
  };

  const handleContainerLayout = useCallback((e: LayoutChangeEvent) => {
    setContainerWidth(e.nativeEvent.layout.width);
  }, []);

  const handleLabelLayout = useCallback(
    (index: number, e: LayoutChangeEvent) => {
      const w = e.nativeEvent.layout.width;
      labelWidthsRef.current[index] = w;
      if (
        labels &&
        labelWidthsRef.current.filter((v) => v > 0).length === labels.length
      ) {
        setLabelWidths([...labelWidthsRef.current]);
      }
    },
    [labels]
  );

  const { visible: visibleLabels, overflowCount } = computeVisibleLabels();

  const renderDescription = () => {
    if (!description) return null;

    if (typeof description === 'string') {
      return (
        <Text style={styles.description} numberOfLines={1}>
          {description}
        </Text>
      );
    }

    return description;
  };

  const cardStyle = [
    styles.card,
    isSelected && styles.cardSelected,
    borderRadiusProp !== undefined && { borderRadius: borderRadiusProp },
  ];

  const activeStyle = {
    pill: { backgroundColor: Colors.role.surface },
    pillLabel: { color: Colors.role.textStrong },
    tag: { backgroundColor: Colors.role.surface },
    tagText: { color: Colors.role.textStrong },
  };

  const renderCardContent = (isActive: boolean) => (
    <>
      {workspaceName && (
        <View style={styles.workspaceRow}>
          <View
            style={[
              styles.workspaceBadge,
              isActive && styles.workspaceBadgeActive,
            ]}
          >
            <Text
              style={[
                styles.workspaceText,
                isActive && styles.workspaceTextActive,
              ]}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {workspaceName}
            </Text>
          </View>
        </View>
      )}

      <View style={styles.bodyRow}>
        {showImage && (
          <View style={styles.imageWrapper}>
            {imageUrl ? (
              <Image source={{ uri: imageUrl }} style={styles.image} />
            ) : (
              <View style={styles.imagePlaceholder}>
                <Icon name="QR" size={20} color={Colors.role.textSecondary} />
              </View>
            )}
          </View>
        )}

        <View style={styles.content}>
          {/* Row 1: Asset name pill */}
          {showAssetName && (
            <View style={styles.headerRow}>
              <View style={styles.assetNamePillWrapper}>
                <Pill
                  label={assetName}
                  icon={QrIcon}
                  disabled={true}
                  style={styles.assetNamePill}
                  styleLabel={styles.assetNameLabelPill}
                  iconSize={10}
                />
              </View>
            </View>
          )}

          {/* Row 2: Severity + reminder + title + pin + date + unread */}
          <View style={styles.titleRow}>
            <View style={styles.leadingIcons}>
              <TypeIcon variant={severityVariant} size={16} />
              {hasReminder && (
                <View style={styles.reminderBadge}>
                  <Icon name="Alert" size={16} color={Colors.role.textStrong} />
                </View>
              )}
            </View>

            <Text style={styles.title} numberOfLines={1}>
              {title}
            </Text>

            {isPinned && (
              <Icon name="Pin" size={16} color={Colors.role.textSecondary} />
            )}

            <Text style={styles.dateText}>{date}</Text>

            {isUnread && (
              <Icon name="Dot" size={12} color={colors.semantic.info} />
            )}

            {trailingAction}
          </View>

          {/* Row 3: Description */}
          {description && (
            <View style={styles.descriptionRow}>{renderDescription()}</View>
          )}

          {/* Hidden measurement row for labels */}
          {labels &&
            labels.length > 0 &&
            labelWidths.length < labels.length && (
              <View style={styles.labelsMeasure} pointerEvents="none">
                {labels.map((label, index) => (
                  <View
                    key={`measure-${label.id}`}
                    onLayout={(e) => handleLabelLayout(index, e)}
                  >
                    <StatusTag variant="neutral" text={label.name} />
                  </View>
                ))}
              </View>
            )}

          {/* Row 4: Labels with overflow */}
          {labels && labels.length > 0 && (
            <View
              style={styles.labelsContainer}
              onLayout={handleContainerLayout}
            >
              {visibleLabels.map((label) => (
                <StatusTag
                  key={label.id}
                  variant="neutral"
                  text={label.name}
                  style={{
                    ...styles.statusTagContainer,
                    ...(isActive ? activeStyle.tag : {}),
                  }}
                  textStyle={isActive ? activeStyle.tagText : undefined}
                />
              ))}
              {overflowCount > 0 && (
                <StatusTag
                  variant="neutral"
                  text={`+${overflowCount}`}
                  style={{
                    ...styles.statusTagContainer,
                    ...(isActive ? activeStyle.tag : {}),
                  }}
                  textStyle={isActive ? activeStyle.tagText : undefined}
                />
              )}
            </View>
          )}
        </View>
      </View>
    </>
  );

  const [isPressed, setIsPressed] = useState(false);
  const pressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handlePressIn = useCallback(() => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
    }
    pressTimerRef.current = setTimeout(() => {
      setIsPressed(true);
    }, 100);
  }, []);

  const handlePressOut = useCallback(() => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
    setIsPressed(false);
  }, []);

  useEffect(() => {
    return () => {
      if (pressTimerRef.current) {
        clearTimeout(pressTimerRef.current);
      }
    };
  }, []);

  if (Platform.OS !== 'web') {
    return (
      <View
        style={[...cardStyle, isPressed && styles.cardPressed]}
        onTouchStart={handlePressIn}
        onTouchEnd={handlePressOut}
        onTouchCancel={handlePressOut}
      >
        {renderCardContent(isPressed)}
      </View>
    );
  }

  return (
    <View
      style={[...cardStyle, isHovered && styles.cardHovered]}
      {...hoverProps}
    >
      {renderCardContent(isHovered)}
    </View>
  );
};

ActivityCard.displayName = 'ActivityCard';

const styles = themedStyleSheet((role) => ({
  card: {
    width: '100%',
    alignSelf: 'stretch',
    backgroundColor: role.surface,
    padding: spacing.lg,
    justifyContent: 'center',
  },
  cardSelected: {
    backgroundColor: role.surfaceSelected,
    borderRadius: 0,
  },
  cardPressed: {
    backgroundColor: role.surfaceInverseSoftHover,
  },
  cardHovered: {
    shadowOpacity: 0.08,
    backgroundColor: role.surfaceInverseSoftHover,
    cursor: 'pointer',
    ...Platform.select({
      android: {
        elevation: 4,
      },
    }),
  },
  workspaceRow: {
    width: '100%',
    marginBottom: spacing.sm,
  },
  workspaceBadge: {
    backgroundColor: role.surfaceActive,
    borderRadius: 4,
    paddingVertical: spacing.xxs,
    paddingHorizontal: spacing.sm,
    alignSelf: 'flex-start',
  },
  workspaceBadgeActive: {
    backgroundColor: role.surface,
  },
  workspaceText: {
    ...typography.label.mediumEmphasised,
    color: role.textStrong,
    lineHeight: 14,
  },
  workspaceTextActive: {
    color: role.textStrong,
  },
  bodyRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    minWidth: 0,
  },
  imageWrapper: {
    width: 66,
    height: 66,
    borderRadius: 4,
    overflow: 'hidden',
    backgroundColor: role.surfaceHover,
    ...Platform.select({
      web: {
        width: 98,
        height: 98,
      },
    }),
  },
  image: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  imagePlaceholder: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    flex: 1,
    gap: spacing.xs,
    minWidth: 0,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    minWidth: 0,
  },
  assetNamePillWrapper: {
    flexShrink: 1,
    flexBasis: 'auto',
    gap: spacing.xs,
    minWidth: 0,
  },
  assetNamePill: {
    backgroundColor: role.surfaceActive,
    borderRadius: 4,
    paddingVertical: spacing.xxs,
    paddingHorizontal: spacing.sm,
  },
  assetNameLabelPill: {
    fontSize: 12,
    lineHeight: spacing.lg,
    color: role.textStrong,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    minWidth: 0,
  },
  leadingIcons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
  },
  reminderBadge: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    flex: 1,
    minWidth: 0,
    ...typography.body.smallEmphasised,
    fontWeight: '600',
    color: role.textStrong,
  },
  dateText: {
    ...typography.label.mediumEmphasised,
    color: role.textSecondary,
    flexShrink: 0,
    marginLeft: 'auto',
  },
  descriptionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    minWidth: 0,
  },
  description: {
    ...typography.body.small,
    color: role.textSecondary,
    flex: 1,
  },
  labelsMeasure: {
    flexDirection: 'row',
    position: 'absolute',
    opacity: 0,
    gap: spacing.sm,
  },
  labelsContainer: {
    flexDirection: 'row',
    gap: spacing.sm,
    overflow: 'hidden',
    ...Platform.select({
      android: {
        marginTop: spacing.xs,
      },
      ios: {
        marginTop: spacing.xs,
      },
    }),
  },
  statusTagContainer: {
    height: 28,
  },
}));
